import { BlockType, BlockModel } from '@empathy/survey/base';

import { QuestionEditor } from '../../../models/question-editor.model';

export const LIST_QUESTION_BATCH_SIZE = 80;

export type ListQuestionRenderRow = ListQuestionWelcomeRow | ListQuestionEndPageRow | ListQuestionPageBreakRow | ListQuestionBlockRow | ListQuestionRow;

export interface ListQuestionRenderModel {
  rowIdByQuestionId: Map<string, string>;
  rowIndexByQuestionId: Map<string, number>;
  rows: ListQuestionRenderRow[];
}

export interface ListQuestionWelcomeRow {
  id: string;
  type: 'welcome';
}

export interface ListQuestionEndPageRow {
  id: string;
  type: 'end';
}

export interface ListQuestionPageBreakRow {
  id: string;
  pageId: string;
  type: 'pageBreak';
}

export interface ListQuestionBlockRow {
  blockView: ListQuestionBlockView;
  id: string;
  type: 'block';
}

export interface ListQuestionRow {
  id: string;
  isFirst: boolean;
  isLast: boolean;
  movePositionAllowed: boolean;
  question: QuestionEditor;
  type: 'question';
}

export interface ListQuestionBlockView {
  block: BlockModel;
  childBlocks: ListQuestionBlockView[];
  questions: ListQuestionBlockQuestion[];
}

export interface ListQuestionBlockQuestion {
  isFirst: boolean;
  isLast: boolean;
  movePositionAllowed: boolean;
  question: QuestionEditor;
  showPageBreak: boolean;
}

interface ListQuestionRenderIndexes {
  blockByQuestionId: Map<string, BlockModel>;
  childBlocksByParentId: Map<string, BlockModel[]>;
  parentBlockByChildId: Map<string, BlockModel>;
  questionIndexById: Map<string, number>;
  questionsByBlockId: Map<string, QuestionEditor[]>;
  totalQuestions: number;
}

export function buildListQuestionRenderModel(questions: QuestionEditor[], blocks: BlockModel[]): ListQuestionRenderModel {
  const indexes = buildIndexes(questions, blocks);
  const renderedBlockIds = new Set<string>();
  const renderedPageIds = new Set<string>();
  const rowIdByQuestionId = new Map<string, string>();
  const rowIndexByQuestionId = new Map<string, number>();
  const rows: ListQuestionRenderRow[] = [{ id: 'welcome', type: 'welcome' }];

  questions.forEach((question, questionIndex) => {
    const block = indexes.blockByQuestionId.get(question.id);

    if (!block) {
      addQuestionRows(rows, rowIndexByQuestionId, rowIdByQuestionId, renderedPageIds, question, questionIndex, questions.length);
      return;
    }

    const topLevelBlock = findTopLevelBlock(block, indexes);

    if (!isFirstBlockOnParentPath(block, indexes)) {
      return;
    }

    if (renderedBlockIds.has(topLevelBlock.id)) {
      return;
    }

    renderedBlockIds.add(topLevelBlock.id);
    addBlockRow(rows, rowIndexByQuestionId, rowIdByQuestionId, renderedPageIds, indexes, topLevelBlock);
  });

  rows.push({ id: 'end', type: 'end' });

  return {
    rowIdByQuestionId,
    rowIndexByQuestionId,
    rows
  };
}

function addBlockRow(
  rows: ListQuestionRenderRow[],
  rowIndexByQuestionId: Map<string, number>,
  rowIdByQuestionId: Map<string, string>,
  renderedPageIds: Set<string>,
  indexes: ListQuestionRenderIndexes,
  block: BlockModel
): void {
  const rowId = `block-${block.id}`;
  const rowIndex = rows.length;

  rows.push({
    blockView: buildBlockView(block, rowId, rowIndex, rowIndexByQuestionId, rowIdByQuestionId, renderedPageIds, indexes),
    id: rowId,
    type: 'block'
  });
}

function addQuestionRows(
  rows: ListQuestionRenderRow[],
  rowIndexByQuestionId: Map<string, number>,
  rowIdByQuestionId: Map<string, string>,
  renderedPageIds: Set<string>,
  question: QuestionEditor,
  questionIndex: number,
  totalQuestions: number
): void {
  addPageBreakRow(rows, renderedPageIds, question);

  const rowId = `question-${question.id}`;
  rowIndexByQuestionId.set(question.id, rows.length);
  rowIdByQuestionId.set(question.id, rowId);
  rows.push({
    id: rowId,
    isFirst: questionIndex === 0,
    isLast: questionIndex === totalQuestions - 1,
    movePositionAllowed: totalQuestions > 1,
    question,
    type: 'question'
  });
}

function addPageBreakRow(rows: ListQuestionRenderRow[], renderedPageIds: Set<string>, question: QuestionEditor): boolean {
  if (renderedPageIds.has(question.pageId)) {
    return false;
  }

  renderedPageIds.add(question.pageId);

  if (question.pageId !== 'page_1' && question.pageId !== '') {
    rows.push({
      id: `page-break-${question.pageId}-${question.id}`,
      pageId: question.pageId,
      type: 'pageBreak'
    });
    return true;
  }

  return false;
}

function buildBlockView(
  block: BlockModel,
  rowId: string,
  rowIndex: number,
  rowIndexByQuestionId: Map<string, number>,
  rowIdByQuestionId: Map<string, string>,
  renderedBlockPageIds: Set<string>,
  indexes: ListQuestionRenderIndexes
): ListQuestionBlockView {
  const childBlocks = (indexes.childBlocksByParentId.get(block.id) ?? []).map((childBlock) =>
    buildBlockView(childBlock, rowId, rowIndex, rowIndexByQuestionId, rowIdByQuestionId, renderedBlockPageIds, indexes)
  );

  const questions = (indexes.questionsByBlockId.get(block.id) ?? []).map((question) => {
    const questionIndex = indexes.questionIndexById.get(question.id);

    rowIndexByQuestionId.set(question.id, rowIndex);
    rowIdByQuestionId.set(question.id, rowId);

    return {
      isFirst: questionIndex === 0,
      isLast: questionIndex === indexes.totalQuestions - 1,
      movePositionAllowed: indexes.totalQuestions > 1,
      question,
      showPageBreak: addPageBreakRow([], renderedBlockPageIds, question)
    };
  });

  return {
    block,
    childBlocks,
    questions
  };
}

function isFirstBlockOnParentPath(block: BlockModel, indexes: ListQuestionRenderIndexes): boolean {
  let currentBlock = block;
  let parentBlock = indexes.parentBlockByChildId.get(currentBlock.id);

  while (parentBlock) {
    if (parentBlock.groupedBlockIds[0] !== currentBlock.id) {
      return false;
    }

    currentBlock = parentBlock;
    parentBlock = indexes.parentBlockByChildId.get(currentBlock.id);
  }

  return true;
}

function findTopLevelBlock(block: BlockModel, indexes: ListQuestionRenderIndexes): BlockModel {
  let topLevelBlock = block;
  let parentBlock = indexes.parentBlockByChildId.get(topLevelBlock.id);

  while (parentBlock) {
    topLevelBlock = parentBlock;
    parentBlock = indexes.parentBlockByChildId.get(topLevelBlock.id);
  }

  return topLevelBlock;
}

function buildIndexes(questions: QuestionEditor[], blocks: BlockModel[]): ListQuestionRenderIndexes {
  const blockByQuestionId = new Map<string, BlockModel>();
  const childBlocksByParentId = new Map<string, BlockModel[]>();
  const parentBlockByChildId = new Map<string, BlockModel>();
  const questionIndexById = new Map<string, number>();
  const questionsByBlockId = new Map<string, QuestionEditor[]>();

  questions.forEach((question, index) => questionIndexById.set(question.id, index));

  blocks.forEach((block) => {
    block.groupedQuestionIds?.forEach((questionId) => {
      if (!blockByQuestionId.has(questionId)) {
        blockByQuestionId.set(questionId, block);
      }
    });
  });

  blocks
    .filter((block) => block.type === BlockType.MultiBlock)
    .forEach((parentBlock) => {
      const childBlocks = blocks.filter((block) => parentBlock.groupedBlockIds?.includes(block.id));
      childBlocksByParentId.set(parentBlock.id, childBlocks);

      childBlocks.forEach((childBlock) => {
        if (!parentBlockByChildId.has(childBlock.id)) {
          parentBlockByChildId.set(childBlock.id, parentBlock);
        }
      });
    });

  blocks
    .filter((block) => block.type !== BlockType.MultiBlock)
    .forEach((block) => {
      questionsByBlockId.set(
        block.id,
        questions.filter((question) => block.groupedQuestionIds?.includes(question.id))
      );
    });

  return {
    blockByQuestionId,
    childBlocksByParentId,
    parentBlockByChildId,
    questionIndexById,
    questionsByBlockId,
    totalQuestions: questions.length
  };
}
